import React, { useMemo, useState } from 'react';
import { Table, type TableColumn } from '@/components/ui/Table';
import { Button } from '@/components/ui/Button/Button';
import { Badge } from '@/components/ui/Badge/Badge';
import { SimpleTooltip } from '@/components/ui/Tooltip';
import { HStack, VStack } from '@/components/ui/Stack';
import { Modal } from '@/components/ui/Modal/Modal';
import { Plane, Trash2, ArrowRight, ArrowRightLeft, X, Clock, FileCheck, XCircle, Plus } from 'lucide-react';
import { VenueCard } from './VenueCard';
import { MoveToDestinationModal, type DestinationOption } from '@/components/venues/MoveToDestinationModal';
import { NoteIcon, NotesEditorModal } from '../../NoteIcon';
import type { ShortlistWithVenue } from '@/services/ResearchService';
import { StarRating } from '@/components/ui/StarRating';

interface ShortlistTableProps {
  items: ShortlistWithVenue[];
  onRemove: (shortlistId: string) => void;
  onUpdateNotes: (shortlistId: string, notes: string, clientFacing: boolean) => void | Promise<void>;
  onViewDetails: (venue: ShortlistWithVenue['venue']) => void;
  onClearAll?: () => void;
  onContinue?: () => void;
  isLoading?: boolean;
  removingId?: string | null;
  // Move functionality
  destinations?: DestinationOption[];
  currentDestinationId?: string;
  onMoveVenue?: (shortlistId: string, destinationId: string) => Promise<void>;
  onCreateDestinationAndMove?: (shortlistId: string, destination: { city: string; country: string; place_id?: string; destination_type?: 'city' | 'region' | 'country' }) => Promise<void>;
  // Add hotel
  onAddHotel?: () => void;
}

export function ShortlistTable({
  items,
  onRemove,
  onUpdateNotes,
  onViewDetails,
  onClearAll,
  onContinue,
  isLoading = false,
  removingId = null,
  destinations = [],
  currentDestinationId,
  onMoveVenue,
  onCreateDestinationAndMove,
  onAddHotel,
}: ShortlistTableProps) {
  const [clearAllConfirm, setClearAllConfirm] = useState(false);
  const [moveModalOpen, setMoveModalOpen] = useState(false);
  const [movingItem, setMovingItem] = useState<ShortlistWithVenue | null>(null);
  const [isMoving, setIsMoving] = useState(false);
  const [editingItem, setEditingItem] = useState<ShortlistWithVenue | null>(null);

  const maxShortlist = 20;
  const count = items.length;
  const canMove = onMoveVenue && destinations.length > 1;

  function handleMoveClick(item: ShortlistWithVenue) {
    setMovingItem(item);
    setMoveModalOpen(true);
  }

  async function handleMoveVenue(destinationId: string) {
    if (!movingItem || !onMoveVenue) return;
    setIsMoving(true);
    try {
      await onMoveVenue(movingItem.id, destinationId);
      setMoveModalOpen(false);
      setMovingItem(null);
    } finally {
      setIsMoving(false);
    }
  }

  async function handleCreateAndMove(destination: { city: string; country: string; place_id?: string; destination_type?: 'city' | 'region' | 'country' }) {
    if (!movingItem || !onCreateDestinationAndMove) return;
    setIsMoving(true);
    try {
      await onCreateDestinationAndMove(movingItem.id, destination);
      setMoveModalOpen(false);
      setMovingItem(null);
    } finally {
      setIsMoving(false);
    }
  }


  const columns = useMemo<TableColumn<ShortlistWithVenue>[]>(() => [
    {
      key: 'property_name',
      title: 'Property Name',
      sortable: true,
      render: (_value: any, record: ShortlistWithVenue) => (
        <div className="flex items-center gap-2">
          <button
            onClick={() => onViewDetails(record.venue)}
            className="font-medium text-secondary-600 hover:text-secondary-700 hover:underline text-left"
          >
            {record.venue.property_name}
          </button>
          {record.cvent_supplier_id && (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-50 text-blue-700 border border-blue-200 whitespace-nowrap">
              Cvent
            </span>
          )}
        </div>
      )
    },
    {
      key: 'number_of_rooms',
      title: 'Rooms',
      sortable: true,
      render: (_value: any, record: ShortlistWithVenue) => 
        record.venue.number_of_rooms?.toLocaleString() || '-'
    },
    {
      key: 'maximum_capacity',
      title: 'Max Capacity',
      sortable: true,
      render: (_value: any, record: ShortlistWithVenue) => 
        record.venue.maximum_capacity?.toLocaleString() || '-'
    },
    {
      key: 'nearby_airports',
      title: 'Nearby Airports',
      render: (_value: any, record: ShortlistWithVenue) => {
        // Check if nearby_airports exists (it might not be in VenueDetails type)
        const airports = (record.venue as any).nearby_airports || [];
        
        // Fallback to nearest_airport if nearby_airports is not available
        if (airports.length === 0 && record.venue.nearest_airport_iata) {
          const distance = record.venue.nearest_airport_distance_km 
            ? Number(record.venue.nearest_airport_distance_km)
            : null;
          
          return (
            <SimpleTooltip
              content={distance
                ? `${record.venue.nearest_airport_name || record.venue.nearest_airport_iata} - ${distance.toFixed(1)}km away`
                : record.venue.nearest_airport_name || record.venue.nearest_airport_iata}
              side="top"
            >
              <div className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs">
                <Plane className="w-3 h-3" />
                <span className="font-medium">{record.venue.nearest_airport_iata}</span>
                {distance && (
                  <span className="text-blue-500">{distance.toFixed(0)}km</span>
                )}
              </div>
            </SimpleTooltip>
          );
        }
        
        if (airports.length === 0) {
          return (
            <div 
              className="inline-flex items-center gap-1 text-gray-400"
              title="No airports assigned"
            >
              <Plane className="w-3 h-3" />
              <span className="text-xs">None</span>
            </div>
          );
        }
        
        return (
          <div className="flex flex-wrap gap-1">
            {airports.slice(0, 2).map((airport: any) => (
              <SimpleTooltip
                key={airport.iata}
                content={`${airport.name} - ${airport.distance_km.toFixed(1)}km away`}
                side="top"
              >
                <div className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs">
                  <Plane className="w-3 h-3" />
                  <span className="font-medium">{airport.iata}</span>
                  <span className="text-blue-500">{airport.distance_km.toFixed(0)}km</span>
                </div>
              </SimpleTooltip>
            ))}
            {airports.length > 2 && (
              <div className="relative">
                <SimpleTooltip
                  content={
                    <div className="space-y-1">
                      <div className="font-medium text-xs">Additional Airports:</div>
                      {airports.slice(2).map((airport: any) => (
                        <div key={airport.iata} className="flex items-center justify-between gap-2 text-xs">
                          <div className="flex items-center gap-1">
                            <Plane className="w-3 h-3" />
                            <span className="font-medium">{airport.iata}</span>
                            <span className="text-gray-500">{airport.name}</span>
                          </div>
                          <span className="text-gray-600">{airport.distance_km.toFixed(1)}km</span>
                        </div>
                      ))}
                    </div>
                  }
                  side="top"
                >
                  <div className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs cursor-help">
                    <span>+{airports.length - 2} more</span>
                  </div>
                </SimpleTooltip>
              </div>
            )}
          </div>
        );
      }
    },
    {
      key: 'star_rating',
      title: 'Stars',
      sortable: true,
      render: (_value: any, record: ShortlistWithVenue) =>
        record.venue.star_rating ? <StarRating rating={record.venue.star_rating} size="sm" showEmptyStars={true} /> : '-'
    },
    {
      key: 'tripadvisor_rating',
      title: 'TripAdvisor',
      render: (_value: any, record: ShortlistWithVenue) => {
        const rating = record.venue.tripadvisor_rating ? Number(record.venue.tripadvisor_rating) : null;

        if (!rating) return '-';

        return (
          <div className="inline-flex items-center gap-1">
            <span className="text-sm text-gray-600">{rating.toFixed(1)}</span>
            {record.venue.tripadvisor_review_count && (
              <span className="text-xs text-gray-500">({record.venue.tripadvisor_review_count})</span>
            )}
          </div>
        );
      }
    },
    {
      key: 'rfp_status',
      title: 'RFP Status',
      render: (_value: any, record: ShortlistWithVenue) => {
        const status = record.cvent_proposal_status?.toLowerCase().replace(/[\s]+/g, '_');
        if (!status) return <span className="text-xs text-gray-400">—</span>;

        if (['submitted_proposal', 'submitted', 'received'].includes(status)) {
          return (
            <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-200">
              <FileCheck className="w-3 h-3" />
              <span>Received</span>
            </div>
          );
        }
        if (['turned_down', 'declined', 'rejected'].includes(status)) {
          return (
            <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-50 text-red-700 border border-red-200">
              <XCircle className="w-3 h-3" />
              <span>Declined</span>
            </div>
          );
        }
        if (['awaiting_proposal', 'pending', 'draft', 'progress'].includes(status)) {
          return (
            <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200">
              <Clock className="w-3 h-3" />
              <span>Awaiting</span>
            </div>
          );
        }
        return <span className="text-xs text-gray-500">{status}</span>;
      }
    },
    {
      key: 'notes',
      title: 'Notes',
      render: (_value: any, record: ShortlistWithVenue) => (
        <NoteIcon
          notes={record.notes}
          clientFacing={record.notes_client_facing}
          venueName={record.venue.property_name}
          onUpdateNotes={(notes, clientFacing) => onUpdateNotes(record.id, notes, clientFacing)}
        />
      )
    },
    {
      key: 'actions',
      title: 'Actions',
      render: (_: any, record: ShortlistWithVenue) => (
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onViewDetails(record.venue)}
            className="text-xs"
          >
            View
          </Button>
          {canMove && (
            <SimpleTooltip content="Move to different destination" side="top">
              <Button
                size="sm"
                variant="ghost"
                onClick={() => handleMoveClick(record)}
                className="text-gray-600 hover:text-primary-600 hover:bg-primary-50"
              >
                <ArrowRightLeft className="w-4 h-4" />
              </Button>
            </SimpleTooltip>
          )}
          {record.cvent_supplier_id ? (
            <SimpleTooltip content="Connected to Cvent — disconnect on RFPs tab to remove" side="top">
              <Button
                size="sm"
                variant="ghost"
                disabled
                className="text-gray-300 cursor-not-allowed"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </SimpleTooltip>
          ) : (
            <SimpleTooltip content="Remove from shortlist" side="top">
              <Button
                size="sm"
                variant="ghost"
                onClick={() => onRemove(record.id)}
                disabled={removingId === record.id}
                isLoading={removingId === record.id}
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </SimpleTooltip>
          )}
        </div>
      )
    }
  ], [onViewDetails, onRemove, removingId, canMove]);

  if (isLoading) {
    return <div className="text-center py-8 text-gray-500">Loading shortlist...</div>;
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <h3 className="text-lg font-semibold text-gray-900">
            Shortlist ({count}/{maxShortlist})
          </h3>
          <div className="flex items-center gap-2 flex-wrap">
            {onAddHotel && count < maxShortlist && (
              <Button
                size="sm"
                variant="outline"
                leftIcon={<Plus className="w-4 h-4" />}
                onClick={onAddHotel}
                className="h-7 px-2 text-xs"
              >
                Add Hotel
              </Button>
            )}
            {count > 0 && onClearAll && (
              <Button
                size="sm"
                variant="ghost"
                leftIcon={<Trash2 className="w-4 h-4" />}
                onClick={() => setClearAllConfirm(true)}
                className="text-gray-600 hover:text-red-600 hover:bg-red-50 h-7 px-2 text-xs"
              >
                Clear All
              </Button>
            )}
            {count > 0 && onContinue && (
              <Button
                variant="primary"
                size="sm"
                rightIcon={<ArrowRight className="w-4 h-4" />}
                onClick={onContinue}
              >
                Continue to RFPs
              </Button>
            )}
          </div>
        </div>
        {count > 0 && onContinue && (
          <p className="text-xs text-center text-muted-foreground mt-2">
            You can always come back to edit your shortlist
          </p>
        )}
      </div>

      {/* Mobile: Card View */}
      <div className="md:hidden">
        {count === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <p className="text-sm">No venues in shortlist</p>
            <p className="text-xs mt-1">Add venues from search results to get started</p>
          </div>
        ) : (
          <VStack gap={3}>
            {items.map((item) => (
              <VenueCard
                key={item.id}
                venue={item.venue}
                isShortlisted={true}
                onRemoveFromShortlist={item.cvent_supplier_id ? undefined : () => onRemove(item.id)}
                onViewDetails={() => onViewDetails(item.venue)}
                onNotesClick={() => setEditingItem(item)}
                showNotes={true}
                hasNotes={!!item.notes}
                showAIScores={false}
              />
            ))}
          </VStack>
        )}
      </div>
      
      {/* Desktop: Table View */}
      <div className="hidden md:block">
        {count === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <p className="text-sm">No venues in shortlist</p>
            <p className="text-xs mt-1">Add venues from search results to get started</p>
          </div>
        ) : (
          <Table
            data={items}
            columns={columns}
            loading={false}
            searchable={false}
            pagination={false}
            emptyText="No venues in shortlist"
          />
        )}
      </div>

      {/* Clear All Confirmation Dialog */}
      {clearAllConfirm && onClearAll && (
        <Modal
          isOpen={true}
          onClose={() => setClearAllConfirm(false)}
          title="Clear All Venues"
          size="sm"
        >
          <VStack gap={4}>
            <p className="text-sm text-gray-600">
              Are you sure you want to remove all venues from your shortlist? This action cannot be undone.
            </p>
            <HStack gap={2} justify="end">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setClearAllConfirm(false)}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                size="sm"
                onClick={() => {
                  onClearAll();
                  setClearAllConfirm(false);
                }}
              >
                Clear All
              </Button>
            </HStack>
          </VStack>
        </Modal>
      )}

      {/* Move to Destination Modal */}
      <MoveToDestinationModal
        isOpen={moveModalOpen}
        onClose={() => {
          setMoveModalOpen(false);
          setMovingItem(null);
        }}
        onMove={handleMoveVenue}
        onCreateAndMove={onCreateDestinationAndMove ? handleCreateAndMove : undefined}
        currentDestinationId={currentDestinationId}
        destinations={destinations}
        venueName={movingItem?.venue.property_name || ''}
        allowCreateNew={!!onCreateDestinationAndMove}
        isMoving={isMoving}
      />

      {/* Notes Modal (mobile cards) — same shared hotel note as the table icon */}
      <NotesEditorModal
        isOpen={editingItem !== null}
        onClose={() => setEditingItem(null)}
        venueName={editingItem?.venue.property_name || ''}
        notes={editingItem?.notes}
        clientFacing={editingItem?.notes_client_facing}
        onSave={(notes, clientFacing) => editingItem ? onUpdateNotes(editingItem.id, notes, clientFacing) : undefined}
      />
    </div>
  );
}

